import React, { useContext, useState } from 'react';
import { KeyOutlined, MailOutlined } from '@ant-design/icons';
import { Button, notification, Popconfirm, Space } from 'antd';

import Auth0UserDetailsContext from '@totem/components/services/remoteAccess/auth0users/details/Auth0UserDetailsContext';
import StepResultsModal from '@totem/components/services/remoteAccess/auth0users/details/StepResultsModal';
import { MultiStepResults } from '@totem/components/services/remoteAccess/auth0users/details/types';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import { securityGroupCheckConstraintAny } from '@totem/components/userContext/security/userUtilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import {
  AUTH0_USER_ENDPOINT,
  REMOTE_ACCESS_USERS_ENDPOINT,
} from '@totem/utilities/endpoints';

const Auth0UserActions = () => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const { user } = useContext(Auth0UserDetailsContext);

  const [sendingReset, setSendingReset] = useState<boolean>(false);
  const [sendingInvite, setSendingInvite] = useState<boolean>(false);
  const [resetResult, setResetResult] = useState<MultiStepResults | null>(null);
  const [showResetResults, setShowResetResults] = useState<boolean>(false);
  const [inviteResult, setInviteResult] = useState<MultiStepResults | null>(
    null,
  );
  const [showInviteResults, setShowInviteResults] = useState<boolean>(false);

  const isRemoteAccessAdmin = permissions.remoteAccessAdmin.hasPermissions(
    userProfile,
    securityGroupCheckConstraintAny,
  );
  const isInternal = permissions.internal.hasPermissions(userProfile);

  const email = user?.item?.email ?? '';

  if (!isRemoteAccessAdmin || !email) {
    return null;
  }

  const handleSendPasswordReset = () => {
    if (sendingReset) {
      return;
    }
    setSendingReset(true);

    fetch(`${AUTH0_USER_ENDPOINT}/${encodeURIComponent(email)}/passwordReset`, {
      method: 'PUT',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: MultiStepResults) => {
        if (isInternal) {
          setResetResult(result);

          setShowResetResults(true);

          return;
        }

        if (result?.success) {
          notification.success({
            message: 'Password reset sent',
            description: `Sent a password reset email to ${email}.`,
          });
        } else {
          notification.error({
            message: 'Error sending password reset',
            description:
              result?.error ??
              'An error occurred while sending the password reset.',
            duration: 0,
          });
        }
      })
      .catch(() => {
        notification.error({
          message: 'Error sending password reset',
          description: 'An error occurred while sending the password reset.',
          duration: 0,
        });
      })
      .finally(() => setSendingReset(false));
  };

  const handleResendInvite = () => {
    if (sendingInvite) {
      return;
    }
    setSendingInvite(true);

    fetch(
      `${REMOTE_ACCESS_USERS_ENDPOINT}/${encodeURIComponent(email)}/invite`,
      {
        method: 'PUT',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      },
    )
      .then((res) => res.json())
      .then((result: MultiStepResults) => {
        if (isInternal) {
          setInviteResult(result);

          setShowInviteResults(true);

          return;
        }

        if (result?.success) {
          notification.success({
            message: 'Invite sent',
            description: `Resent the invite to ${email}.`,
          });
        } else {
          notification.error({
            message: 'Error resending invite',
            description:
              result?.error ?? 'An error occurred while resending the invite.',
            duration: 0,
          });
        }
      })
      .catch(() => {
        notification.error({
          message: 'Error resending invite',
          description: 'An error occurred while resending the invite.',
          duration: 0,
        });
      })
      .finally(() => setSendingInvite(false));
  };

  return (
    <>
      <Space>
        <Popconfirm
          title="Send password reset?"
          description={`Send a password reset email to ${email}?`}
          okText="Send"
          onConfirm={handleSendPasswordReset}
        >
          <Button icon={<KeyOutlined />} loading={sendingReset}>
            Send Password Reset
          </Button>
        </Popconfirm>
        <Popconfirm
          title="Resend user invite?"
          description={`Resend the remote access invite to ${email}?`}
          okText="Resend"
          onConfirm={handleResendInvite}
        >
          <Button icon={<MailOutlined />} loading={sendingInvite}>
            Resend User Invite
          </Button>
        </Popconfirm>
      </Space>
      <StepResultsModal
        visible={showResetResults}
        title="Send Password Reset Result"
        result={resetResult}
        successMessage="Password reset sent"
        failureMessage="Send password reset failed"
        onClose={() => setShowResetResults(false)}
      />
      <StepResultsModal
        visible={showInviteResults}
        title="Resend Invite Result"
        result={inviteResult}
        successMessage="Invite sent"
        failureMessage="Resend invite failed"
        onClose={() => setShowInviteResults(false)}
      />
    </>
  );
};

export default Auth0UserActions;
